import React, { useEffect, useState } from "react"
import { BASE_URL } from "../../../Config/api"
import {
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    Button,
    Modal,
    Typography,
    CircularProgress,
    TextField,
    Box,
    Stack,
    Chip,
    Stepper,
    Step,
    StepLabel,
    DialogContentText,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
} from "@mui/material"
import {
    fetchLeaveApplications,
    fetchLeaveApplicationCounts,
    changeLeaveStatus,
    changeEndDate,
    skipPendingApprover,
} from "../../../Actions/commonController"
import PaginationTable from "../../Pagination"
import { useAuth } from "../../../Context/authContext"
import { toast } from "react-toastify"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"
import CircleIcon from "@mui/icons-material/Circle"
import { FormatDate } from "../../Utilities/DateFormatter"
import SearchBar from "../../SearchBar"
import ResponsiveDatePicker from "../../DatePicker/ResponsiveDatePicker"
import dayjs from "dayjs"
import DownloadIcon from "@mui/icons-material/Download"
import VisibilityIcon from "@mui/icons-material/Visibility"
import DescriptionIcon from "@mui/icons-material/Description"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import ImageIcon from "@mui/icons-material/Image"

const columns = [
    { id: "ID", label: "SR NO.", minWidth: 65 },
    { id: "ID", label: "EMPID", minWidth: 70 },
    { id: "name", label: "NAME", minWidth: 100 },
    { id: "type", label: "LEAVE TYPE", minWidth: 130 },
    { id: "startDate", label: "START DATE", minWidth: 100 },
    { id: "endDate", label: "END DATE", minWidth: 90 },
    { id: "duration", label: "DURATION", minWidth: 100 },
    { id: "reason", label: "REASON", minWidth: 100 },
    { id: "status", label: "STATUS", minWidth: 100 },
    { id: "requestDate", label: "REQUEST DATE", minWidth: 90 },
    { id: "actions", label: "ACTIONS", minWidth: 200 },
]

export default function LeaveApplicationsTable() {
    const [page, setPage] = useState(1)
    const [quantity, setQuantity] = useState(10)
    const [totalItems, setTotalItems] = useState(0)
    const [data, setData] = useState([])
    const [leaveRequests, setLeaveRequests] = useState([])
    const [initialLoad, setInitialLoad] = useState(false)
    const [reasonModalOpen, setReasonModalOpen] = useState(false)
    const [actionModalOpen, setActionModalOpen] = useState(false)
    const [trackerModalOpen, setTrackerModalOpen] = useState(false)
    const [selectedReason, setSelectedReason] = useState("")
    const [selectedLeave, setSelectedLeave] = useState(null)
    const [onConfirm, setOnConfirm] = useState("")
    const [loading, setLoading] = useState(false)
    const [actionType, setActionType] = useState("")
    const [leaveTypeFilter, setLeaveTypeFilter] = useState("all")
    const [approvedBy, setApprovedBy] = useState("")
    const [rejectReason, setRejectReason] = useState("")
    const [rejectModalOpen, setRejectModalOpen] = useState(false)
    const { eid } = useAuth()
    const [statusFilter, setStatusFilter] = useState("all")
    const [loader, setLoader] = useState(false)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [newEndDate, setNewEndDate] = useState("")
    const [searchTerm, setSearchTerm] = useState("")
    const [skipModalOpen, setSkipModalOpen] = useState(false)
    const [skipLeaveId, setSkipLeaveId] = useState(null)
    const [skipEmpId, setSkipEmpId] = useState(null)
    const [selectedRejectInfo, setSelectedRejectInfo] = useState({
        rejectedByName: "",
        rejectedreason: "",
    })
    const [trackerData, setTrackerData] = useState({
        pending: [],
        accepted: [],
        rejected: [],
    })

    const getFileIcon = (filename) => {
        const extension = filename.split('.').pop().toLowerCase()
        switch (extension) {
            case 'pdf':
                return <PictureAsPdfIcon />
            case 'doc':
            case 'docx':
                return <DescriptionIcon />
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
                return <ImageIcon />
            default:
                return <InsertDriveFileIcon />
        }
    }

    const handleViewCertificate = () => {
        if (selectedLeave && selectedLeave.medicalDocUrl) {
            window.open(selectedLeave.medicalDocUrl, '_blank', 'noopener,noreferrer');
        } else if (selectedLeave && selectedLeave.medicalDoc) {
            try {
                const medicalDoc = JSON.parse(selectedLeave.medicalDoc);
                const filename = medicalDoc.filename;
                const fullUrl = `${BASE_URL}/uploads/medical-documents/${filename}`;
                window.open(fullUrl, '_blank', 'noopener,noreferrer');
            } catch (error) {
                console.error('Error viewing medical document:', error);
                toast.error("Error viewing certificate");
            }
        } else {
            toast.info("No document attached");
        }
    };

    const handleUpdateEndDate = async () => {
        try {
            const formattedNewEndDate = new Date(newEndDate).toISOString().split('T')[0]
            let APINewDate = {
                leaveid: selectedLeave.id,
                empId: selectedLeave.empid,
                startDate: selectedLeave.startdate,
                newEndDate: formattedNewEndDate,
                duration: selectedLeave.duration,
                type: selectedLeave.type,
            }
            setLoading(true)
            const { status } = await changeEndDate(APINewDate)
            if (status === "success") {
                toast.success("End date updated successfully")
                setEditModalOpen(false)
                loadData()
            } else {
                toast.error("New end date must be before the existing end date")
            }
        } catch (error) {
            toast.error("An error occurred while updating end date", error)
        }
        setLoading(false)
    }

    const initialization = async () => {
        try {
            const APIData = {
                empid: eid,
                status: statusFilter,
                search: searchTerm,
                leaveType: leaveTypeFilter,
            }
            setLoader(true)
            const { data, status } = await fetchLeaveApplicationCounts(APIData)
            if (status === "success") {
                setTotalItems(data)
                loadData()
            } else {
                toast.error("Failed to fetch leave count.")
            }
        } catch (error) {
            toast.error("Error fetching leave count")
        }
    }

    const loadData = async () => {
        try {
            let APIData = {
                empid: eid,
                page: page,
                quantity: quantity,
                status: statusFilter,
                search: searchTerm,
                leaveType: leaveTypeFilter,
            }
            setLoader(true)
            let { data, status } = await fetchLeaveApplications(APIData)
            if (status === "success") {
                const relevantRequestsForUser = data.filter((request) => {
                    const pendingList = request.pendingapproval ? request.pendingapproval.split(",") : [];
                    const acceptedList = request.acceptedapproval ? request.acceptedapproval.split(",") : [];
                    const rejectedList = request.rejectedby ? request.rejectedby.split(",") : [];
                    return (
                        pendingList[0] === eid.toString() ||
                        acceptedList.includes(eid.toString()) ||
                        rejectedList.includes(eid.toString())
                    );
                });

                setLeaveRequests(relevantRequestsForUser);
            } else {
                console.error("Error fetching employee leave applications", status)
            }
        } catch (error) {
            console.error("Error in empLeave function", error)
        }
        setLoader(false)
    }

    const handleOption = async () => {
        try {
            setLoading(true)
            let APIData = {
                action: actionType.toLowerCase(),
                leaveid: onConfirm,
                empid: eid,
                reason: rejectReason,
            }
            const { status, message } = await changeLeaveStatus(APIData)
            if (status === "success") {
                setActionModalOpen(false)
                setRejectReason("")
                toast.success(message)
                loadData()
            }
        } catch (err) {
            toast.error(err.message)
        }
        setLoading(false)
        setReasonModalOpen(false)
        setOnConfirm("")
    }

    const handleSearch = async (term) => {
        setSearchTerm(term)
    }

    const handleFilter = (status) => {
        setStatusFilter(status)
    }

    const handleLeaveTypeFilter = (type) => {
        setLeaveTypeFilter(type)
        setPage(1)
    }

    const handlePageClick = (newPage) => {
        setPage(newPage)
    }

    const handleItemsPerPageChange = (newRowsPerPage) => {
        setQuantity(newRowsPerPage)
        setPage(1)
    }

    const handleButtonClick = (leaveid, action, empid, dayLength) => {
        setOnConfirm(leaveid)
        setActionType(action)
        setApprovedBy(empid)
        setActionModalOpen(true)
    }

    const handleRejectInfo = (rejectedByName, rejectedreason) => {
        setSelectedRejectInfo({ rejectedByName, rejectedreason })
        setRejectModalOpen(true)
    }

    const handleCloseModal = () => {
        setReasonModalOpen(false)
        setActionModalOpen(false)
        setTrackerModalOpen(false)
        setRejectModalOpen(false)
        setOnConfirm("")
    }

    const handleReasonButton = (item) => {
        setSelectedReason(item.reason)
        setSelectedLeave(item)
        setReasonModalOpen(true)
    }

    const handleTrackerButtonClick = (leave) => {
        const tracker = {
            pending: leave.pendingNames ? leave.pendingNames.split(",") : [],
            accepted: leave.acceptedNames ? leave.acceptedNames.split(",") : [],
            rejected: leave.rejectedNames ? leave.rejectedNames.split(",") : [],
        }
        setTrackerData(tracker)
        console.log("Tracker Data:", tracker)
        setTrackerModalOpen(true)
    }

    const handleSkipApprover = (leaveid, empid) => {
        setSkipLeaveId(leaveid)
        setSkipEmpId(empid)
        setSkipModalOpen(true)
    }

    const confirmSkipApprover = async () => {
        try {
            setLoading(true)
            let APIData = {
                leaveid: skipLeaveId,
                empid: skipEmpId,
            }
            const { status, message } = await skipPendingApprover(APIData)
            if (status === "success") {
                toast.success("Approver skipped successfully")
                loadData()
            } else {
                toast.error(message || "Failed to skip approver")
            }
        } catch (error) {
            console.error("Error skipping approver:", error)
            toast.error("Error occurred while skipping approver")
        } finally {
            setLoading(false)
            setSkipModalOpen(false)
            setSkipLeaveId(null)
            setSkipEmpId(null)
        }
    }

    useEffect(() => {
        if (initialLoad) loadData()
        setInitialLoad(true)
    }, [page, quantity])

    useEffect(() => {
        initialization()
    }, [eid, statusFilter, leaveTypeFilter, searchTerm])

    const renderTracker = () => {
        const steps = [
            ...trackerData.accepted.map((name) => ({ name, status: "approved" })),
            ...trackerData.pending.map((name) => ({ name, status: "pending" })),
            ...trackerData.rejected.map((name) => ({ name, status: "rejected" })),
        ]

        return (
            <Box sx={{ width: "100%" }}>
                <Stepper alternativeLabel>
                    {steps.map((step, index) => (
                        <Step key={index} active>
                            <StepLabel
                                StepIconComponent={() =>
                                    step.status === "approved" ? (
                                        <CheckCircleIcon sx={{ color: "var(--secondary-color)" }} />
                                    ) : step.status === "rejected" ? (
                                        <CancelIcon color="error" />
                                    ) : (
                                        <HourglassEmptyIcon color="warning" />
                                    )
                                }
                            >
                                {step.name}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>
        )
    }

    const chipColor = (status) => {
        switch (status.toLowerCase()) {
            case "pending":
                return "primary"
            case "cancelled":
                return "warning"
            case "rejected":
                return "error"
            case "accepted":
                return "success"
            default:
                return "default"
        }
    }

    const getLeaveTypeColor = (leaveType) => {
        switch (leaveType.toLowerCase()) {
            case "annual":
                return "blue"
            case "sick":
                return "green"
            case "paternity":
                return "purple"
            case "maternity":
                return "pink"
            case "emergency":
                return "red"
            default:
                return "gray"
        }
    }

    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
    }

    return (
        <>
            <Paper className="pagePaper">
                <span className="info">
                    <h5>Manage Leave Applications</h5>
                    <SearchBar searchText="Search by name..." onSearch={handleSearch} searchType="name" />
                </span>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "30px" }}>
                    <Button
                        variant="contained"
                        onClick={() => handleFilter("all")}
                        sx={{
                            backgroundColor: statusFilter === "all" ? "var(--secondary-color)" : "transparent",
                            color: statusFilter === "all" ? "white" : "var(--secondary-color)",
                            marginRight: "10px",
                            "&:hover": {
                                backgroundColor: "var(--secondary-color)",
                                color: "white",
                            },
                        }}
                    >
                        All
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => handleFilter("Pending")}
                        sx={{
                            backgroundColor: statusFilter === "Pending" ? "var(--secondary-color)" : "transparent",
                            color: statusFilter === "Pending" ? "white" : "var(--secondary-color)",
                            marginRight: "10px",
                            "&:hover": {
                                backgroundColor: "var(--secondary-color)",
                                color: "white",
                            },
                        }}
                    >
                        Pending
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => handleFilter("Accepted")}
                        sx={{
                            backgroundColor: statusFilter === "Accepted" ? "var(--secondary-color)" : "transparent",
                            color: statusFilter === "Accepted" ? "white" : "var(--secondary-color)",
                            marginRight: "10px",
                            "&:hover": {
                                backgroundColor: "var(--secondary-color)",
                                color: "white",
                            },
                        }}
                    >
                        Accepted
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => handleFilter("Rejected")}
                        sx={{
                            backgroundColor: statusFilter === "Rejected" ? "var(--secondary-color)" : "transparent",
                            color: statusFilter === "Rejected" ? "white" : "var(--secondary-color)",
                            marginRight: "10px",
                            "&:hover": {
                                backgroundColor: "var(--secondary-color)",
                                color: "white",
                            },
                        }}
                    >
                        Rejected
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => handleFilter("Cancelled")}
                        sx={{
                            backgroundColor: statusFilter === "Cancelled" ? "var(--secondary-color)" : "transparent",
                            color: statusFilter === "Cancelled" ? "white" : "var(--secondary-color)",
                            "&:hover": {
                                backgroundColor: "var(--secondary-color)",
                                color: "white",
                            },
                        }}
                    >
                        Cancelled
                    </Button>
                </div>

                <div className="row" style={{
                    paddingTop: "60px",
                }}>
                    <div className="col-md-12">
                        <TableContainer
                            sx={{
                                overflowY: "auto",
                                maxHeight: "500px"
                            }}
                        >
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell
                                                className="tableHeader"
                                                key={column.id}
                                                align="center"
                                                style={{
                                                    minWidth: column.minWidth,
                                                    backgroundColor: "var(--card-color)",
                                                    color: "var(--primary-text-color)",
                                                }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loader ? (
                                        <TableRow>
                                            <TableCell colSpan={11} style={{ padding: "30px 0", textAlign: "center" }}>
                                                <CircularProgress size={100} style={{ color: "var(--secondary-color)" }} />
                                            </TableCell>
                                        </TableRow>
                                    ) : leaveRequests && leaveRequests.length > 0 ? (
                                        leaveRequests.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell align="center" className="p-0"> {item.id}</TableCell>
                                                <TableCell align="center" className="p-0"> {item.empid}</TableCell>
                                                <TableCell align="center" className="p-0"> {item.name}</TableCell>
                                                <TableCell align="center" className="p-0">
                                                    <Box display="flex" justifyContent="flex-start" sx={{ paddingLeft: 2, width: "100%" }}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <CircleIcon sx={{ color: getLeaveTypeColor(item.type), fontSize: 14 }} />
                                                            <Typography variant="body2" align="left">
                                                                {item.type}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" className="p-0"> {FormatDate(item.startdate)}</TableCell>
                                                <TableCell align="center" className="p-0"> {FormatDate(item.enddate)}</TableCell>
                                                <TableCell align="center" className="p-0"> {item.duration}</TableCell>
                                                <TableCell align="center" className="p-0">
                                                    <Button variant="contained" className="tableButton" onClick={() => { handleReasonButton(item) }}>
                                                        View
                                                    </Button>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        <Chip label={capitalizeFirstLetter(item.status)} color={chipColor(item.status)} />
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>{FormatDate(item.createdAt)}</TableCell>
                                                <TableCell>
                                                    {item.status === "pending" ? (
                                                        <>
                                                            {(() => {
                                                                const pendingList = item.pendingapproval.split(',').filter(p => p)
                                                                const acceptedList = item.acceptedapproval.split(',').filter(p => p)
                                                                const currentManagerIndex = pendingList.indexOf(eid)

                                                                if (currentManagerIndex === 0 || currentManagerIndex === acceptedList.length) {
                                                                    return (
                                                                        <>
                                                                            <Button
                                                                                variant="contained"
                                                                                className="btn success"
                                                                                onClick={() => handleButtonClick(item.id, "Accepted")}
                                                                            >
                                                                                Accept
                                                                            </Button>
                                                                            <Button
                                                                                variant="contained"
                                                                                className="btn rejected"
                                                                                onClick={() => handleButtonClick(item.id, "Rejected")}
                                                                            >
                                                                                Reject
                                                                            </Button>
                                                                            <Button
                                                                                variant="contained"
                                                                                className="btn tracker"
                                                                                onClick={() => handleTrackerButtonClick(item)}
                                                                            >
                                                                                Tracker
                                                                            </Button>
                                                                        </>
                                                                    )
                                                                } else {
                                                                    return (
                                                                        <>
                                                                            <Button
                                                                                variant="contained"
                                                                                className="btn tracker"
                                                                                onClick={() => handleTrackerButtonClick(item)}
                                                                            >
                                                                                Tracker
                                                                            </Button>
                                                                            <Button
                                                                                variant="contained"
                                                                                className="btn skip"
                                                                                onClick={() => handleSkipApprover(item.id, eid)}
                                                                            >
                                                                                Skip Approver
                                                                            </Button>
                                                                        </>
                                                                    )
                                                                }
                                                            })()}
                                                        </>
                                                    ) : item.status === "accepted" ? (
                                                        <>
                                                            <Button
                                                                variant="contained"
                                                                className="btn warning"
                                                                onClick={() => {
                                                                    setSelectedLeave(item)
                                                                    setNewEndDate(item.endDate)
                                                                    setEditModalOpen(true)
                                                                }}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </>
                                                    ) : item.status === "rejected" ? (
                                                        <Button
                                                            variant="contained"
                                                            className="btn rjct"
                                                            onClick={() => handleRejectInfo(item.rejectedByName, item.rejectedreason)}
                                                        >
                                                            Info
                                                        </Button>
                                                    ) : item.status === "cancelled" ? (
                                                        "-"
                                                    ) : (
                                                        "-"
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={11} style={{ padding: "30px 0", textAlign: "center", color: "var(--text-primary)" }}>
                                                No data found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <PaginationTable
                            totalItems={totalItems}
                            itemsPerPage={quantity}
                            onPageChange={handlePageClick}
                            onItemsPerPageChange={handleItemsPerPageChange}
                        />
                    </div>
                </div>
            </Paper>

            {/* Reason Modal */}
            <Modal
                open={reasonModalOpen}
                onClose={handleCloseModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Paper className="floatingPaper" sx={{ maxWidth: 600, width: '90%', p: 3 }}>
                    <Typography id="modal-modal-title" variant="h6" component="h2" gutterBottom>
                        Reason
                    </Typography>
                    <Typography id="modal-modal-description" sx={{ mt: 2, mb: 3 }}>
                        {selectedReason}
                    </Typography>

                    {/* Medical Certificate Section */}
                    {selectedLeave && (selectedLeave.medicalDoc || selectedLeave.medicalDocUrl) && (
                        <>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                Attached Document
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                                {selectedLeave.medicalDoc && (() => {
                                    try {
                                        const medicalDoc = JSON.parse(selectedLeave.medicalDoc)
                                        return getFileIcon(medicalDoc.filename)
                                    } catch {
                                        return <InsertDriveFileIcon />
                                    }
                                })()}
                                <a
                                    href={selectedLeave.medicalDocUrl || '#'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleViewCertificate();
                                    }}
                                    style={{
                                        color: "var(--secondary-color)",
                                        textDecoration: "none",
                                        marginLeft: "8px",
                                        fontWeight: 500,
                                        cursor: "pointer"
                                    }}
                                    onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                                    onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                                >
                                    {selectedLeave.medicalDoc ?
                                        (() => {
                                            try {
                                                const medicalDoc = JSON.parse(selectedLeave.medicalDoc)
                                                return medicalDoc.originalname || medicalDoc.filename || 'Medical Document'
                                            } catch {
                                                return 'Medical Document'
                                            }
                                        })() :
                                        'Medical Document'
                                    }
                                </a>
                            </Box>
                        </>
                    )}
                </Paper>
            </Modal>

            {/* Tracker Modal */}
            <Modal
                open={trackerModalOpen}
                onClose={handleCloseModal}
                aria-labelledby="tracker-modal-title"
                aria-describedby="tracker-modal-description"
            >
                <Paper className="floatingPaper" sx={{ maxWidth: 600, width: '90%', p: 3 }}>
                    <Typography id="tracker-modal-title" variant="h6" component="h2">
                        Approval Tracker
                    </Typography>
                    <Typography id="tracker-modal-description" sx={{ mt: 2 }}>
                        {renderTracker()}
                    </Typography>
                </Paper>
            </Modal>

            {/* Confirmation Modal */}
            <Modal
                open={actionModalOpen}
                onClose={handleCloseModal}
                aria-labelledby="confirmation-modal-title"
                aria-describedby="confirmation-modal-description"
            >
                <Paper className="floatingPaper">
                    <Typography id="confirmation-modal-title" variant="h6" component="h2">
                        {actionType === "Accepted" ? "Accept Leave" : "Reject Leave"}
                    </Typography>
                    <Typography id="confirmation-modal-description" sx={{ my: 2, color: "gray" }}>
                        Are you sure you want to {actionType === "Accepted" ? "Accept" : "Reject"} this leave application?
                    </Typography>
                    {actionType === "Rejected" && (
                        <TextField
                            autoFocus
                            margin="dense"
                            id="reason"
                            label="Reason"
                            type="text"
                            multiline
                            rows={4}
                            fullWidth
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                        />
                    )}
                    <div style={{ display: "flex", marginTop: "25px", width: "370px", justifyContent: "space-between" }}>
                        <Button
                            variant="contained"
                            onClick={handleCloseModal}
                            style={{ backgroundColor: "#FF0000", color: "white", width: "60px", height: "35px" }}
                        >
                            No
                        </Button>
                        <Button
                            sx={{
                                backgroundColor: "var(--secondary-color)",
                                color: "white",
                                width: "60px",
                                height: "35px",
                            }}
                            onClick={handleOption}
                            autoFocus
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Yes"}
                        </Button>
                    </div>
                </Paper>
            </Modal>

            {/* Rejection Info Modal */}
            <Modal
                open={rejectModalOpen}
                onClose={handleCloseModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "var(--card-color)",
                        borderRadius: 2,
                        border: "none",
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Typography id="modal-title" variant="h6" component="h2">
                        Reason for Rejection
                    </Typography>
                    <Typography id="modal-description" sx={{ mt: 2 }}>
                        {selectedRejectInfo.rejectedreason}
                    </Typography>
                    <Typography sx={{ mt: 2, fontStyle: "italic" }}>
                        Rejected by: {selectedRejectInfo.rejectedByName}
                    </Typography>
                </Box>
            </Modal>

            {/* Edit End Date Modal */}
            <Dialog
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                aria-labelledby="edit-modal-title"
                maxWidth="sm"
            >
                <DialogTitle id="edit-modal-title">Edit End Date</DialogTitle>
                <DialogContent>
                    <DialogContentText>Add a new end date</DialogContentText>
                    <div style={{ marginTop: "16px", textAlign: "center" }}>
                        <ResponsiveDatePicker
                            name="newEndDate"
                            label="End Date"
                            defValue={newEndDate ? dayjs(newEndDate).format("DD/MM/YYYY") : ""}
                            handleDate={(event) => {
                                const selectedDate = event.target.value;

                                if (selectedDate && dayjs(selectedDate, "DD/MM/YYYY", true).isValid()) {
                                    const formattedDate = dayjs(selectedDate, "DD/MM/YYYY").format("YYYY-MM-DD");
                                    setNewEndDate(formattedDate);
                                } else {
                                    setNewEndDate(null);
                                }
                            }}
                            min={selectedLeave ? dayjs(selectedLeave.startdate).format("DD/MM/YYYY") : ""}
                        />
                    </div>
                </DialogContent>
                <DialogActions
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "0px 20px 20px 20px",
                    }}
                >
                    <Button
                        style={{
                            backgroundColor: "#FF0000",
                            color: "white",
                            width: "80px",
                            height: "35px",
                        }}
                        onClick={() => setEditModalOpen(false)}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ backgroundColor: "var(--secondary-color)", color: "white", width: "80px", height: "35px" }}
                        onClick={handleUpdateEndDate}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Skip Approver Modal */}
            <Modal
                open={skipModalOpen}
                onClose={() => setSkipModalOpen(false)}
                aria-labelledby="skip-approver-modal-title"
                aria-describedby="skip-approver-modal-description"
            >
                <Paper className="floatingPaper">
                    <Typography id="skip-approver-modal-title" variant="h6" component="h2">
                        Skip Approver
                    </Typography>
                    <Typography id="skip-approver-modal-description" sx={{ my: 2, color: "gray" }}>
                        Are you sure you want to skip the next approver?
                    </Typography>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
                        <Button
                            variant="contained"
                            onClick={() => setSkipModalOpen(false)}
                            style={{ backgroundColor: "#FF0000", color: "white", width: "80px" }}
                        >
                            No
                        </Button>
                        <Button
                            variant="contained"
                            onClick={confirmSkipApprover}
                            sx={{ backgroundColor: "var(--secondary-color)", color: "white", width: "80px" }}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Yes"}
                        </Button>
                    </div>
                </Paper>
            </Modal>

        </>
    )
}